/**
 * The checks on one file, as quiet rows (R3 T6a). Things to look at are always
 * shown; the passed and not-checked rows fold behind a one-line summary, so a
 * long list stays calm. Not checked is never drawn as passed.
 */

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronRight, CircleDashed } from 'lucide-react'
import { checkSummary } from '@/lib/submission-files-text'
import { cn } from '@/lib/utils'
import type { FileCheck, FileCheckStatus } from '@/types/submission-files'

const STATUS_ICON: Record<FileCheckStatus, { icon: typeof CheckCircle2; className: string; label: string }> = {
  pass: { icon: CheckCircle2, className: 'text-[hsl(var(--status-success))]', label: 'Passed' },
  attention: { icon: AlertTriangle, className: 'text-[hsl(var(--status-warning))]', label: 'Look at this' },
  unchecked: { icon: CircleDashed, className: 'text-subtle', label: 'Not checked' },
}

function CheckRow({ check }: { check: FileCheck }) {
  const { icon: Icon, className, label } = STATUS_ICON[check.status]
  return (
    <li className="flex items-start gap-1.5" data-check={check.key} data-status={check.status}>
      <Icon aria-label={label} className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', className)} />
      <span className={cn('text-meta', check.status === 'attention' && 'text-foreground')}>{check.label}</span>
    </li>
  )
}

interface SubmissionFileChecksProps {
  checks: FileCheck[]
}

export function SubmissionFileChecks({ checks }: SubmissionFileChecksProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  // The "file" row is the file line itself; the rest are the checks.
  const shown = checks.filter((c) => c.key !== 'file')
  const attention = shown.filter((c) => c.status === 'attention')
  const quiet = shown.filter((c) => c.status !== 'attention')
  if (shown.length === 0) return null

  return (
    <div className="mt-1.5 space-y-1">
      {attention.length > 0 && (
        <ul className="space-y-1">
          {attention.map((check) => (
            <CheckRow key={check.key} check={check} />
          ))}
        </ul>
      )}
      {quiet.length > 0 && (
        <>
          <button
            type="button"
            className="inline-flex items-center gap-0.5 text-meta hover:text-foreground"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((open) => !open)}
          >
            <ChevronRight
              aria-hidden
              className={cn('h-3.5 w-3.5 text-subtle transition-transform', isExpanded && 'rotate-90')}
            />
            {checkSummary(quiet)}
          </button>
          {isExpanded && (
            <ul className="space-y-1 pl-4">
              {quiet.map((check) => (
                <CheckRow key={check.key} check={check} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
